import { createContext, useCallback, useContext } from 'react';
import { LearnerPretestStatus, LearnerTrainingStatus } from '~/data';
import { TrainingState, useTrainingStore } from '~/training/state';

const SequenceAnswersContext = createContext<string | undefined>(undefined);
export const SequenceAnswersProvider = SequenceAnswersContext.Provider;

const SurveySlideContext = createContext(false);
/** Marks a course slide that has a survey type. */
export const SurveySlideProvider = SurveySlideContext.Provider;

/**
 * Whether a question is a survey: a llama survey, or any question on a slide
 * with a survey type, however it is marked. The server never scores either.
 */
export const useSurveyQuestion = (llamaSurvey: boolean) =>
  useContext(SurveySlideContext) || llamaSurvey;

/**
 * A survey question once the learner completed a pretest in this attempt:
 * optional, as the server treats it, so it never holds them on its slide and
 * is answered without right or wrong, whichever form the survey takes.
 */
export const useOptionalSurvey = (llamaSurvey: boolean) => {
  const survey = useSurveyQuestion(llamaSurvey);
  const pretestCompleted = useTrainingStore(
    s => s.pretest?.status === LearnerPretestStatus.Completed
  );

  return survey && pretestCompleted;
};

/**
 * Whether the learner's answer to a question is saved: while the course runs,
 * and after a pretest ended it early, for an optional survey only. The server
 * keeps those alone, ungraded, and would drop any other answer.
 */
export const answersSaved = (
  s: Pick<TrainingState, 'status'>,
  optionalSurvey: boolean
) => s.status !== LearnerTrainingStatus.Completed || optionalSurvey;

export const useSaveQuestionAnswers = () => {
  const chapterSequenceId = useContext(SequenceAnswersContext);
  const save = useTrainingStore(s => s.saveQuestionAnswers);

  return useCallback(
    (questionFieldId: string) => {
      if (chapterSequenceId) save(questionFieldId, chapterSequenceId);
    },
    [save, chapterSequenceId]
  );
};
